// ─────────────────────────────────────────────────────────────────────────────
// 🔑 THE LOAN DISCLOSURES CARD — the LE / CD rows with their signing status ("Pending Your Signature", "Signed",
// "Accepted") and "Review & Sign". Moved out of pages/Documents.js (2026-10-16) so the classic page and the guided
// Documents page render the SAME card: the guided page first shipped without it, and an issued LE lost its
// "Pending Your Signature" there (found by the offer-to-closing E2E). test/documentsLeStatus holds both.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { openDocument } from '../utils/openDocument';

// 🚨 'generated' IS THE NATIVE BUILDER'S STATUS; 'created' was DocMagic's (test/documentsLeStatus).
export const LE_CD_READY_STATUSES = ['generated', 'created'];

export default function LoanDisclosuresCard({ token, docs, dmDocs }) {
  const navigate = useNavigate();
  const [signLoading] = useState(null); // 'le' | 'cd' | null
  const handleReviewAndSign = (docType) => {
    if (docType === 'cd') {
      navigate('/portal/closing-disclosures');
    } else {
      navigate('/dashboard');
    }
  };
  docs = docs || [];
  if (!dmDocs || dmDocs.length === 0) return null;
    const itpSignedDoc = docs.find(d => d.document_type === 'itp_signed_le');
    // closing_disclosure_signed (NOT closing_disclosure) — the View
    // Signed CD button below uses this id to fetch the stamped-signed
    // PDF the same way the LE row uses itp_signed_le. The unsigned
    // draft stays admin-side; the signed copy is what the borrower
    // sees alongside the LE-signed link.
    const cdSignedDoc = docs.find(d => d.document_type === 'closing_disclosure_signed');
    return (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
  <div className="px-5 py-4 border-b border-gray-50">
  <h2 className="font-semibold text-gray-900">Loan Disclosures</h2>
  <p className="text-xs text-gray-500 mt-0.5">Review and e-sign your official loan disclosures.</p>
  </div>
  <div className="p-5 space-y-3">
  {['le', 'cd'].map(t => {
    const doc = dmDocs.find(d => d.doc_type === t);
    if (!doc) return null;
    const label = t === 'le' ? 'Loan Estimate' : 'Closing Disclosure';
    // ITP-accepted LE overrides DocMagic's package status.
    const itpAccepted = t === 'le' && !!itpSignedDoc;
    const signedAt = itpAccepted
      ? (itpSignedDoc.upload_date || itpSignedDoc.created_at)
      : doc.all_signed_at;
    const signed = itpAccepted || !!doc.all_signed_at;
    // 🚨 'generated' IS THE NATIVE BUILDER'S STATUS (routes/disclosures.js, since DocMagic was
    // retired); 'created' was DocMagic's. Reading only 'created' showed every native LE to the
    // borrower as "Not Yet Available" (2026-10-16, found by the final E2E).
    const awaitingSig = !signed && LE_CD_READY_STATUSES.includes(doc.package_status);
    const declined = doc.package_status === 'declined';
    const pillBg = itpAccepted ? '#acf44e' : signed ? '#acf44e' : awaitingSig ? '#ffdd86' : declined ? '#fecdd3' : '#f3f4f6';
    const pillColor = signed ? '#1a1a1a' : awaitingSig ? '#7a5200' : declined ? '#9f1239' : '#374151';
    const pillLabel = itpAccepted ? 'Accepted' : signed ? 'Signed' : awaitingSig ? 'Pending Your Signature' : declined ? 'Declined' : 'Not Yet Available';
    const busy = signLoading === t;
    // 🔑 The real PDF at a signed link in the app's viewer — not a blob (utils/openDocument, Jesse 2026-10-28).
    const viewSignedItp = () => {
      if (!itpSignedDoc) return;
      openDocument({ id: itpSignedDoc.id, name: 'Signed Loan Estimate' }, token, navigate).catch(() => alert('Signed Loan Estimate not available.'));
    };
    // Mirror viewSignedItp for the CD — fetches the stamped+cert-
    // appended PDF stored as closing_disclosure_signed.
    const viewSignedCd = () => {
      if (!cdSignedDoc) return;
      openDocument({ id: cdSignedDoc.id, name: 'Signed Closing Disclosure' }, token, navigate).catch(() => alert('Signed Closing Disclosure not available.'));
    };
    return (
      <div key={t} className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-gray-100">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">{label}</p>
          <div className="flex items-center gap-2 mt-1">
            <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 700, background: pillBg, color: pillColor }}>{pillLabel}</span>
            {signedAt && <span className="text-xs text-gray-500">on {new Date(signedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
          </div>
        </div>
        {awaitingSig && (
          <button
            onClick={() => handleReviewAndSign(t)}
            disabled={busy}
            style={{
              padding: '8px 16px',
              background: busy ? '#e5e7eb' : '#3a6ad9',
              color: busy ? '#9ca3af' : '#fff',
              border: 'none', borderRadius: 8,
              cursor: busy ? 'wait' : 'pointer',
              fontSize: 13, fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', gap: 8,
            }}>
            {busy ? (
              <>
                <span style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.5)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.6s linear infinite' }} />
                Opening…
              </>
            ) : 'Review & Sign'}
          </button>
        )}
        {itpAccepted && (
          <button
            onClick={viewSignedItp}
            style={{ padding: '8px 16px', background: '#fff', color: '#3a6ad9', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            View Signed LE
          </button>
        )}
        {/* View Signed CD — mirrors View Signed LE. Only renders for
            the CD row when the signed-and-stamped PDF exists on
            file. The LE row uses itpAccepted; the CD row uses the
            cdSignedDoc presence directly (no ITP equivalent). */}
        {t === 'cd' && signed && cdSignedDoc && (
          <button
            onClick={viewSignedCd}
            style={{ padding: '8px 16px', background: '#fff', color: '#3a6ad9', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            View Signed CD
          </button>
        )}
        {/* Plain ✓ fallback only for non-CD rows when there's no
            dedicated viewer button — keeps the LE row's behavior
            for non-ITP signing paths. */}
        {signed && !itpAccepted && t !== 'cd' && <span className="text-sm text-green-600 font-semibold">✓</span>}
      </div>
    );
  })}
  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
  </div>
    );
  }
